import HttpFactory from "~/repository/factory";
import type {
  AddBeneficiaryPayload,
  AutoApprovalResult,
  UpdateBeneficiaryPayload,
  BancoConveniado,
  CreateProposalPayload,
  DesfechoNovasCondicoesResponse,
  FinalizeProposalPayload,
  RegistrarDesfechoNovasCondicoesPayload,
  FullProposalResponse,
  PaymentDetails,
  Proposal,
  ProposalBeneficiary,
  ProposalListItem,
  ProposalPayment,
  ProposalLeadData,
  ReplicateBeneficiariesPayload,
  SavePaymentPayload,
  UpdateProposalPayload,
} from "~/types/proposal";

class ProposalModule extends HttpFactory {
  private RESOURCE = "/proposal";

  async getLeadData(leadId: string): Promise<ProposalLeadData> {
    return await this.get<ProposalLeadData>(`${this.RESOURCE}/lead-data/${leadId}`);
  }

  async countByLead(leadId: string): Promise<number> {
    return await this.get<number>(`${this.RESOURCE}/count?lead_id=${leadId}`);
  }

  async getProposalsByLeadId(leadId: string): Promise<Proposal[]> {
    return await this.get<Proposal[]>(`${this.RESOURCE}/by-lead/${leadId}`);
  }

  async getProposalListByLeadId(leadId: string): Promise<ProposalListItem[]> {
    return await this.get<ProposalListItem[]>(`${this.RESOURCE}/by-lead/${leadId}/summary`);
  }

  async getOrCreateByLeadStudy(leadId: string, studyId: string): Promise<FullProposalResponse> {
    return await this.post<FullProposalResponse>(`${this.RESOURCE}/by-lead-study`, {
      lead_id: leadId,
      study_id: studyId,
    });
  }

  async createProposal(data: CreateProposalPayload): Promise<Proposal> {
    return await this.post<Proposal>(this.RESOURCE, data);
  }

  async updateProposal(id: string, data: UpdateProposalPayload): Promise<Proposal> {
    return await this.put<Proposal>(`${this.RESOURCE}/${id}`, data);
  }

  async getFullProposal(id: string): Promise<FullProposalResponse> {
    return await this.get<FullProposalResponse>(`${this.RESOURCE}/${id}`);
  }

  async addBeneficiary(
    id: string,
    data: AddBeneficiaryPayload,
  ): Promise<ProposalBeneficiary> {
    return await this.post<ProposalBeneficiary>(`${this.RESOURCE}/${id}/beneficiaries`, data);
  }

  async getBeneficiaries(id: string): Promise<ProposalBeneficiary[]> {
    return await this.get<ProposalBeneficiary[]>(`${this.RESOURCE}/${id}/beneficiaries`);
  }

  async updateBeneficiary(
    id: string,
    beneficiaryId: string,
    data: UpdateBeneficiaryPayload,
  ): Promise<ProposalBeneficiary> {
    return await this.put<ProposalBeneficiary>(
      `${this.RESOURCE}/${id}/beneficiaries/${beneficiaryId}`,
      data,
    );
  }

  async deleteBeneficiary(
    id: string,
    beneficiaryId: string,
  ): Promise<{ success: boolean }> {
    return await this.delete<{ success: boolean }>(
      `${this.RESOURCE}/${id}/beneficiaries/${beneficiaryId}`,
    );
  }

  async replicateBeneficiaries(
    id: string,
    data: ReplicateBeneficiariesPayload,
  ): Promise<ProposalBeneficiary[]> {
    return await this.post<ProposalBeneficiary[]>(
      `${this.RESOURCE}/${id}/replicate-beneficiaries`,
      data,
    );
  }

  async savePayment(id: string, data: SavePaymentPayload): Promise<ProposalPayment> {
    return await this.post<ProposalPayment>(`${this.RESOURCE}/${id}/payment`, data);
  }

  async getPayments(id: string): Promise<ProposalPayment[]> {
    return await this.get<ProposalPayment[]>(`${this.RESOURCE}/${id}/payment`);
  }

  async getAutoApprovalEvaluation(id: string): Promise<AutoApprovalResult> {
    return await this.get<AutoApprovalResult>(`${this.RESOURCE}/${id}/auto-approval-evaluation`);
  }

  async finalizeProposal(
    id: string,
    data?: FinalizeProposalPayload,
  ): Promise<Proposal> {
    return await this.post<Proposal>(`${this.RESOURCE}/${id}/finalize`, data);
  }

  async getBancosConveniados(coverage?: string): Promise<BancoConveniado[]> {
    const query = coverage ? { coverage } : {};
    return await this.get<BancoConveniado[]>(`${this.RESOURCE}/bancos-conveniados`, { query });
  }

  async getPaymentDetails(proposalId: string): Promise<PaymentDetails> {
    return await this.get<PaymentDetails>(`${this.RESOURCE}/${proposalId}/payment-details`);
  }

  async confirmPayment(proposalId: string): Promise<{
    success: boolean;
    seg_pagamento_status?: string | null;
    vida_status?: string | null;
  }> {
    return await this.post(`${this.RESOURCE}/${proposalId}/confirm-payment`);
  }

  async sendPaymentLink(proposalId: string): Promise<{ success: boolean }> {
    return await this.post<{ success: boolean }>(
      `${this.RESOURCE}/${proposalId}/send-payment-link`,
    );
  }

  /**
   * Força o populamento dos detalhes de pagamento (busca QR PIX se ausente).
   * Diferente de `confirmPayment`, NÃO captura cobrança nem dispara mudança
   * de status — só preenche `seg_pagamento_detalhes` quando o cron ainda
   * não rodou (caso típico ao abrir o modal logo após assinar).
   */
  async refreshPaymentDetails(proposalId: string): Promise<{
    success: boolean;
    seg_pagamento_detalhes: Record<string, unknown> | null;
  }> {
    return await this.post(
      `${this.RESOURCE}/${proposalId}/payment-details/refresh`,
    );
  }

  async regenerateCheckout(proposalId: string): Promise<{
    success: boolean;
    checkoutUrl: string;
    seg_pagamento_detalhes: Record<string, unknown>;
  }> {
    return await this.post(
      `${this.RESOURCE}/${proposalId}/regenerate-checkout`,
    );
  }

  /**
   * Registra o desfecho das novas condições de uma cotação agravada (UC092).
   *
   * O `vida_status` da resposta é o do **momento do registro**, não o resultado: no
   * aceite ele ainda é "Novas Condições", porque o despacho roda fora do request path.
   * Quem consome deve olhar `desfecho`/`registrado_em` e acompanhar a transição pelo SSE.
   *
   * Erros: 409 (fora de "Novas Condições" ou desfecho já registrado), 422 (aceite sem
   * data, data futura, ou anterior ao `dataAgravo` do snapshot), 403 (lead de outro
   * corretor). AR-465.
   */
  async registrarDesfechoNovasCondicoes(
    proposalId: string,
    payload: RegistrarDesfechoNovasCondicoesPayload,
  ): Promise<DesfechoNovasCondicoesResponse> {
    return await this.post<DesfechoNovasCondicoesResponse>(
      `${this.RESOURCE}/${proposalId}/novas-condicoes/desfecho`,
      payload,
    );
  }

  async getAllProposals(params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    vendedorId?: string;
  }): Promise<ProposalListItem[]> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.vendedorId) query.set('vendedorId', params.vendedorId);
    const qs = query.toString();
    const url = qs ? `${this.RESOURCE}/all?${qs}` : `${this.RESOURCE}/all`;
    return await this.get<ProposalListItem[]>(url);
  }
}

export default ProposalModule;
